import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { ArrowUpRight, CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General Inquiry",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("Message received. We'll be in touch.");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#050505] text-white">
      <Seo
        title="Contact — Talk to a consultant directly"
        description="Reach E-Quantum Consults directly at support@equantumconsults.com. Publishing questions, royalty recovery, partnerships, press."
        path="/contact"
      />
      <section className="border-b border-white/10 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag mb-6">// Contact</div>
          <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter">
            Get in touch.
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-4">
            {[
              { icon: Mail, label: "Email", value: "hello@equantum.com" },
              { icon: Phone, label: "Phone", value: "+1 (555) 014-2042" },
              { icon: MapPin, label: "Headquarters", value: "Atlanta · Los Angeles" },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="border border-white/10 bg-[#0a0a0a] p-6 card-hover">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)]">
                    <Icon size={16} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      {label}
                    </div>
                    <div className="text-zinc-200">{value}</div>
                  </div>
                </div>
              </div>
            ))}
            <div className="border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-2">
                Faster path
              </div>
              <p className="text-zinc-200 text-sm mb-4">
                Book a free 30-min consultation directly on our calendar.
              </p>
              <a href="/booking" className="btn-primary text-xs">
                Book a call <ArrowUpRight size={14} />
              </a>
            </div>
          </aside>

          <div className="lg:col-span-8 border border-white/10 bg-[#0a0a0a] p-8 md:p-10">
            {success ? (
              <div data-testid={TIDS.contactSuccess} className="text-center py-10">
                <div className="w-14 h-14 mx-auto mb-6 border border-[var(--primary)]/60 bg-[var(--primary)]/10 flex items-center justify-center">
                  <CheckCircle2 className="text-[var(--primary)]" size={24} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-black text-3xl tracking-tighter mb-3">
                  Message received.
                </h3>
                <p className="text-zinc-400">
                  We'll reply within 1 business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    Name
                  </label>
                  <input
                    required
                    data-testid={TIDS.contactName}
                    value={form.name}
                    onChange={update("name")}
                    className="input-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    Email
                  </label>
                  <input
                    required
                    type="email"
                    data-testid={TIDS.contactEmail}
                    value={form.email}
                    onChange={update("email")}
                    className="input-gold"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    Subject
                  </label>
                  <input
                    data-testid={TIDS.contactSubject}
                    value={form.subject}
                    onChange={update("subject")}
                    className="input-gold"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    Message
                  </label>
                  <textarea
                    required
                    rows={6}
                    data-testid={TIDS.contactMessage}
                    value={form.message}
                    onChange={update("message")}
                    className="input-gold"
                    placeholder="Tell us what you're working on…"
                  />
                </div>
                <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 mt-2">
                  <p className="text-xs text-zinc-500">We reply within 1 business day.</p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={TIDS.contactSubmit}
                    className="btn-primary"
                  >
                    {submitting ? "Sending…" : "Send message"} <ArrowUpRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
